import { formatDateTime, TextButton } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { connectionActions, integrationsQuery, SYNC_STATUS, syncable } from "../../connections";
import { messageFor } from "../../error-copy";
import { meQuery } from "../../session";
import styles from "../customers/customers.module.css";

/**
 * Whether a record reached QuickBooks: its status, its QuickBooks id, the last error, and Sync
 * now (P8-33). For people who manage integrations, in a company that has QuickBooks.
 */
export function QuickbooksStatus({ entityType, entityId }: { entityType: string; entityId: string }): ReactNode {
  const { data: me } = useSuspenseQuery(meQuery);
  const allowed = me?.permissions.includes("integrations.manage") ?? false;
  const integrations = useQuery({ ...integrationsQuery, enabled: allowed });
  const books = (integrations.data ?? []).filter((i) => i.provider === "quickbooks");
  const links = useQuery({
    queryKey: ["integrations", "links", entityType, entityId],
    enabled: allowed && books.length > 0,
    queryFn: async () =>
      (
        await ok(
          api.GET("/v1/admin/accounting/links", {
            params: { query: { entity_type: entityType, entity_id: entityId } },
          }),
        )
      ).data,
  });
  if (!allowed || books.length === 0 || !links.data || !syncable(entityType)) return null;
  const link = links.data[0];
  return (
    <section className={styles.form} aria-label="QuickBooks">
      <h3 className={styles.title}>QuickBooks</h3>
      <SyncLine entityType={entityType} entityId={entityId} link={link ?? null} />
    </section>
  );
}

function SyncLine({
  entityType,
  entityId,
  link,
}: {
  entityType: Parameters<typeof connectionActions.syncAgain>[0];
  entityId: string;
  link: {
    sync_status: keyof typeof SYNC_STATUS;
    external_id: string | null;
    last_error: string | null;
    last_synced_at: string | null;
  } | null;
}) {
  const format = useCompanyFormat();
  const queryClient = useQueryClient();
  const sync = useMutation({
    mutationFn: () => connectionActions.syncAgain(entityType, entityId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["integrations"] }),
  });
  const [label, tone] = link ? SYNC_STATUS[link.sync_status] : ["Not sent yet", "neutral" as const];
  return (
    <div className={styles.stack}>
      <span className={styles.actions}>
        <Badge tone={tone}>{label}</Badge>
        {link?.external_id ? <span className={styles.muted}>QuickBooks id {link.external_id}</span> : null}
        {link?.last_synced_at ? (
          <span className={styles.muted}>last synced {formatDateTime(link.last_synced_at, format)}</span>
        ) : null}
      </span>
      {link?.last_error ? <span className={styles.danger}>{link.last_error}</span> : null}
      {link?.sync_status === "synced" ? null : sync.isSuccess ? (
        <span className={styles.muted}>Queued: the status changes when QuickBooks answers.</span>
      ) : (
        <span>
          <TextButton disabled={sync.isPending} onClick={() => sync.mutate()}>
            Sync now
          </TextButton>
        </span>
      )}
      {sync.error ? <span className={styles.danger}>{messageFor(errorOf(sync.error))}</span> : null}
    </div>
  );
}
